import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Sparkles, Search, Package } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EMPTY_PRODUCT = {
  nom: '', description: '', type: 'location',
  univers: [], images: [''],
  tarifs: { particulier: 0, entreprise: 0, association: 0, ce: 0, groupe: 0 },
  disponible: true, featured: false, order_weight: 0,
  tags: [], faq: [], seo_title: '', seo_description: ''
};

export default function AdminCatalogue() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const [enriching, setEnriching] = useState(null);

  useEffect(() => { fetchProducts(); }, [search]); // eslint-disable-line

  async function fetchProducts() {
    setLoading(true);
    let url = `${BACKEND}/api/products?limit=50`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    fetch(url).then(r => r.json()).then(d => { setProducts(d.products || []); setTotal(d.total || 0); }).catch((err) => { /* eslint-disable-line no-empty */ }).finally(() => setLoading(false));
  }

  async function saveProduct() {
    setSaving(true);
    try {
      const method = editProduct ? 'PUT' : 'POST';
      const url = editProduct ? `${BACKEND}/api/products/${editProduct.id}` : `${BACKEND}/api/products`;
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (res.ok) {
        toast.success(editProduct ? 'Produit mis à jour' : 'Produit créé');
        setShowForm(false); setEditProduct(null); setForm(EMPTY_PRODUCT); fetchProducts();
      } else throw new Error('Erreur');
    } catch (e) { toast.error('Erreur sauvegarde'); } finally { setSaving(false); }
  }

  async function deleteProduct(id) {
    if (!window.confirm('Supprimer ce produit ?')) return;
    await fetch(`${BACKEND}/api/products/${id}`, { method: 'DELETE' });
    toast.success('Produit supprimé'); fetchProducts();
  }

  async function enrichProduct(id) {
    setEnriching(id);
    try {
      const res = await fetch(`${BACKEND}/api/ia/enrichir-produit/${id}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) { toast.success('Produit enrichi par IA !'); fetchProducts(); }
    } catch (e) { toast.error('Erreur IA'); } finally { setEnriching(null); }
  }

  function openEdit(p) {
    setEditProduct(p);
    setForm({ ...p, univers: p.univers || [], images: p.images?.length ? p.images : [''], tags: p.tags || [] });
    setShowForm(true);
  }

  return (
    <div data-testid="admin-products-table">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Produits</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Catalogue & IA</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Produits enrichis par IA</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-xl font-bold" style={{ color: 'var(--admin-text)' }}>Catalogue produits</h2>
          <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{total} produits</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditProduct(null); setForm(EMPTY_PRODUCT); }} className="btn-gold px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2">
          <Plus size={16} /> Nouveau produit
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--admin-faint)' }} />
        <input type="text" placeholder="Rechercher un produit..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
      </div>

      {/* Form */}
      {showForm && (
        <div className="mb-6 p-5 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid rgba(212,175,55,0.3)' }}>
          <h3 className="font-semibold mb-4" style={{ color: 'var(--admin-gold)' }}>{editProduct ? 'Modifier le produit' : 'Nouveau produit'}</h3>

          {/* Alerte règle catalogue */}
          <div className="flex items-start gap-2 p-3 rounded-xl mb-4"
            style={{ background: 'var(--admin-gold-dim)', border: '1px solid rgba(212,175,55,0.25)' }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>📋</span>
            <div>
              <p className="text-xs font-bold" style={{ color: 'var(--admin-gold)' }}>Règle catalogue</p>
              <p className="text-xs" style={{ color: 'var(--admin-muted)', lineHeight: 1.5 }}>
                Utiliser les <strong>noms exacts</strong> du catalogue officiel. Les produits de type <strong>Location</strong> affichent <em>"Sur devis"</em> en front. Seuls les produits <strong>Vente</strong> affichent un prix public.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Nom * <span style={{ color: '#FF5C6C' }}>(nom exact catalogue)</span></label>
              <input type="text" value={form.nom} onChange={e => setForm(p => ({ ...p, nom: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
            </div>
            <div>
              <label className="block text-xs mb-1 flex items-center gap-1" style={{ color: 'var(--admin-muted)' }}>
                Type produit * <span style={{ color: '#FF5C6C', fontSize: 10 }}>(obligatoire)</span>
              </label>
              <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm"
                style={{
                  background: '#1C1C1C',
                  border: `2px solid ${form.type === 'location' ? '#5DBCBF' : form.type === 'vente' ? '#D4AF37' : form.type === 'fabrication' ? '#F4A7B2' : '#7DB1FF'}`,
                  color: form.type === 'location' ? '#5DBCBF' : form.type === 'vente' ? '#D4AF37' : form.type === 'fabrication' ? '#F4A7B2' : '#7DB1FF',
                  fontWeight: 700,
                }}>
                <option value="location">📦 Location — affiche "Sur devis"</option>
                <option value="vente">🛒 Vente — affiche le prix</option>
                <option value="precommande">⏳ Précommande — affiche le prix</option>
                <option value="fabrication">✨ Fabrication sur-mesure — "Sur devis"</option>
              </select>
              <p className="text-xs mt-1" style={{ color: form.type === 'location' ? '#5DBCBF' : form.type === 'vente' ? '#D4AF37' : 'rgba(255,255,255,0.3)' }}>
                {form.type === 'location' && '→ Prix masqué en front, "Sur devis" affiché'}
                {form.type === 'vente' && '→ Prix affiché en front (tarifs ci-dessous)'}
                {form.type === 'fabrication' && '→ Prix masqué en front, "Sur devis" affiché'}
                {form.type === 'precommande' && '→ Prix affiché en front avec badge précommande'}
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Description</label>
              <textarea rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm resize-none" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
            </div>
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Image URL</label>
              <input type="url" value={form.images?.[0] || ''} onChange={e => setForm(p => ({ ...p, images: [e.target.value] }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
            </div>
            <div>
              <label className="block text-xs mb-2" style={{ color: 'var(--admin-muted)' }}>Options</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.disponible} onChange={e => setForm(p => ({ ...p, disponible: e.target.checked }))} /> Disponible
                </label>
                <label className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.featured} onChange={e => setForm(p => ({ ...p, featured: e.target.checked }))} /> Featured
                </label>
              </div>
            </div>
            {/* Tarifs — uniquement utiles pour vente/precommande */}
            <div className="sm:col-span-2">
              <label className="block text-xs mb-2" style={{ color: 'var(--admin-muted)' }}>
                Tarifs (€)
                {(form.type === 'location' || form.type === 'fabrication') && (
                  <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-medium"
                    style={{ background: 'rgba(93,188,191,0.15)', color: 'var(--admin-teal)' }}>
                    Tarifs internes uniquement (non affichés en front)
                  </span>
                )}
              </label>
              <div className="grid grid-cols-5 gap-2">
                {['particulier', 'entreprise', 'association', 'ce', 'groupe'].map(k => (
                  <div key={k}>
                    <label className="block text-xs mb-1" style={{ color: 'var(--admin-faint)' }}>{k.slice(0,5)}.</label>
                    <input type="number" value={form.tarifs?.[k] || ''} onChange={e => setForm(p => ({ ...p, tarifs: { ...p.tarifs, [k]: parseFloat(e.target.value) || 0 } }))}
                      className="w-full px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => { setShowForm(false); setEditProduct(null); }} className="px-4 py-2 rounded-xl text-sm" style={{ background: '#2A2A2A', color: 'rgba(255,255,255,0.6)' }}>Annuler</button>
            <button onClick={saveProduct} disabled={saving || !form.nom.trim()} className="flex-1 btn-gold py-2 rounded-xl text-sm font-semibold disabled:opacity-50">{saving ? 'Sauvegarde...' : editProduct ? 'Mettre à jour' : 'Créer le produit'}</button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 rounded-full" style={{ border: '2px solid #2A2A2A', borderTopColor: '#D4AF37' }} /></div>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #2A2A2A' }}>
          {products.map((p, i) => (
            <div key={p.id} className="flex items-center gap-3 px-4 py-3" style={{ background: i % 2 === 0 ? '#161616' : '#121212', borderBottom: '1px solid rgba(42,42,42,0.5)' }}>
              <img src={p.images?.[0] || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=60&q=70'} alt={p.nom} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" loading="lazy" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--admin-text)' }}>{p.nom}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs px-1.5 py-0.5 rounded font-semibold"
                    style={{
                      background: p.type === 'location' ? 'rgba(93,188,191,0.15)' : p.type === 'vente' ? 'rgba(212,175,55,0.15)' : 'rgba(244,167,178,0.15)',
                      color: p.type === 'location' ? '#5DBCBF' : p.type === 'vente' ? '#D4AF37' : '#F4A7B2',
                    }}>
                    {p.type === 'location' ? '📦 Location' : p.type === 'vente' ? '🛒 Vente' : p.type === 'fabrication' ? '✨ Fabrication' : '⏳ Précommande'}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--admin-faint)' }}>
                    {p.type === 'location' || p.type === 'fabrication' ? 'Sur devis' : `${p.tarifs?.particulier || 0}€`}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => enrichProduct(p.id)} disabled={enriching === p.id} className="p-1.5 rounded-lg transition-colors" style={{ background: 'var(--admin-gold-dim)', color: 'var(--admin-gold)' }} title="Enrichir avec IA">
                  {enriching === p.id ? <div className="w-4 h-4 rounded-full animate-spin" style={{ border: '1.5px solid #D4AF37', borderTopColor: 'transparent' }} /> : <Sparkles size={14} />}
                </button>
                <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg" style={{ background: 'rgba(197,216,249,0.1)', color: '#C5D8F9' }}><Edit size={14} /></button>
                <button onClick={() => deleteProduct(p.id)} className="p-1.5 rounded-lg" style={{ background: 'rgba(255,92,108,0.1)', color: '#FF5C6C' }}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
